'use client';

import 'react-phone-input-2/lib/style.css';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import NextImage from 'next/image';
import { Controller, useForm } from 'react-hook-form';
import { LoaderCircle, Mail } from 'lucide-react';

import { TERMS_OF_SERVICE_LINK } from 'app/_data';
import { useLandingApplyMutation } from 'generated/hooks';
import {
  CommunityNode,
  ApplicationFieldNode,
  LandingBenefitsInput,
} from 'generated/types';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { LabeledPhoneInput } from 'components/ui/phone';
import { Textarea } from 'components/ui/textarea';
import { useFormErrorHandling } from 'hooks/useFormErrorHandling';
import { useTracking } from 'app/_tracking';

type ApplyFormProps = {
  isPreview?: boolean
  landingFields: ApplicationFieldNode[];
  community: Pick<CommunityNode, 'id' | 'subdomain' | 'name' | 'logo' | 'joinPolicy'>;
  onJoin?: (inviteLink?: string | null) => void
}

export const ApplyForm: FC<ApplyFormProps> = (props) => {
  /*
  Handling cases:
  1. JoinPolicy is 'AFTER_JOIN`:
    - has invite link => redirect to invite link
    - no invite link => show success modal
  2. JoinPolicy is 'AFTER_APPLY':
    - show modal
  */
  const [isSent, setIsSent] = useState(false);
  const { trackEvent } = useTracking();
  const [applicationFieldsState, applyLanding] = useLandingApplyMutation();
  const { handleErrors } = useFormErrorHandling();

  const onJoinDefault = (inviteLink?: string | null) => {
    if (inviteLink) {
      window.location.href = inviteLink;
    }
  };

  const onJoin = props.onJoin ? props.onJoin : onJoinDefault;

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
    setError,
    clearErrors
  } = useForm({
    mode: 'all'
  });

  useEffect(() => {
    reset();
  }, []);

  const fields = props.landingFields.length === 0 ? [{
    id: 'default',
    orderId: 0,
    label: 'Name',
    name: 'name',
    placeholder: 'John Doe',
    required: true,
    type: 'TEXT',
  } as ApplicationFieldNode] : props.landingFields;

  const onSubmitApplicationForm = useCallback(async (data: any) => {
    try {
      const formValues = fields.map((item) => {
        return {
          fieldId: item.name ?? '',
          value: data[item.name as string] ?? '',
          fieldLabel: item.label ?? ''
        };
      });
      const response = await applyLanding({
        values: formValues,
        communityId: props.community.id
      });
      trackEvent({ name: 'application_submitted' });

      if (response.error) {
        return handleErrors<LandingBenefitsInput>(response.error, setError, {});
      }

      if (props.community.joinPolicy == 'AFTER_APPLY') {
        onJoin(response.data?.landingApply?.inviteLink);
        setIsSent(true);
        return;
      }

      setIsSent(true);
    } catch (error) {
      console.error(error);
      setError('root', { message: 'Something went wrong: ' + error });
    }
  }, [applyLanding, fields, handleErrors, onJoin, props.community.id, props.community.joinPolicy, setError, trackEvent]);

  const applicationFormSubmitButtonTitle = useMemo(() => {
    switch (props.community.joinPolicy) {
      case 'AFTER_APPROVE':
        return 'Submit';
      case 'AFTER_APPLY':
        return 'Join';
      default:
        return 'Finish & Send';
    }
  }, [props.community.joinPolicy]);

  if (isSent) {
    return <SentComponent />;
  }

  return (
    <div className='p-6xl md:p-3 sm:p-0'>
      <div className='px-4xl md:p-4'>
        <div className='flex flex-col gap-3xl'>
          <div className='flex gap-2 self-center items-center'>
            {!!props.community.logo && (
              <NextImage src={props.community.logo} width={41} height={41} alt='Community-logo' />
            )}
          </div>
          <div className='flex flex-col gap-lg items-center'>
            <p className='text-black text-display-sm font-semibold'>{props.community.name}</p>
            <p className='text-gray-400 text-medium'>Fill in the Application</p>
          </div>
          <div>

            <div className='pt-4xl flex flex-col gap-6'>
              <form className='flex flex-col gap-6'
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (props.isPreview) return;
                      clearErrors();
                      handleSubmit(onSubmitApplicationForm)(e);
                    }}
              >
                {fields.map((field, index) => field.type === 'TEXTAREA' ? (
                  <Controller
                    key={index}
                    name={field.name}
                    control={control}
                    rules={{ required: field.required ? 'This field is required' : false }}
                    render={(props) => (
                      <Textarea
                        required={field.required}
                        label={field.label || ''}
                        placeholder={field.placeholder ?? ''}
                        errorText={errors[field.name as string]?.message as string}
                        rows={3}
                        {...props.field}
                      />
                    )}
                  />
                ) : field.type === 'PHONE' ? (
                  <Controller
                    key={index}
                    name={field.name ?? ''}
                    control={control}
                    render={(props) => (
                      <LabeledPhoneInput
                        required={field.required}
                        label={field.label || ''}
                        placeholder={field.placeholder ?? ''}
                        errorText={errors[field.name as string]?.message as string}
                        defaultValue={''}
                        {...props.field}
                      />
                    )}
                  />
                ) : (
                  <Controller
                    key={index}
                    name={field.name}
                    control={control}
                    rules={{ required: field.required ? 'This field is required' : false }}
                    defaultValue={''}
                    render={(props) => (
                      <Input
                        type={field.type.toLowerCase()}
                        required={field.required}
                        label={field.label || ''}
                        placeholder={field.placeholder ?? ''}
                        errorText={errors[field.name as string]?.message as string}
                        {...props.field}
                      />
                    )}
                  />
                ))}
                {!!errors.root && (
                  <p className={`text-small text-error-600`}>{errors.root.message}</p>
                )}
                <div className='flex flex-col gap-3'>
                  <Button
                    disabled={applicationFieldsState.fetching}
                    type='submit'
                    size='large'
                    variant='primaryBrand'
                  >
                    {applicationFieldsState.fetching && <LoaderCircle className='animate-spin' />}
                    {applicationFormSubmitButtonTitle}
                  </Button>
                  <div className='flex flex-col justify-center'>
                    <p className='text-[12px]/[18px] text-gray-600 text-center'>
                      By pressing &lsquo;{applicationFormSubmitButtonTitle}&rsquo;, you agree to the
                    </p>
                    <Button
                      className='p-0'
                      variant='nakedBrandLink'
                      type='button'
                      onClick={(event) => {
                        event.preventDefault();
                        window.open(TERMS_OF_SERVICE_LINK, '_blank');
                      }}
                    >
                      Terms and Conditions.
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SentComponent = () => {
  return (
    <div className='p-6xl md:p-3 sm:p-0'>
      <div className='flex flex-col gap-8 items-center text-gray-900'>
        <div className='flex justify-center items-center border border-gray-200 gap-3 w-14 h-14 rounded-xl'>
          <Mail width={28} height={28} />
        </div>
        <p className='text-display-sm font-semibold'>Thank you for applying</p>
        <div className={'text-center'}>
          <p className='text-medium text-gray-600'>
            You will receive a confirmation email shortly.
          </p>
        </div>
      </div>
    </div>
  );
};
